import { useEffect, useRef, useState } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { voiceApi } from '@/lib/api';

export const MicButton = ({ onText, testId = 'mic-button', className = '' }) => {
  const [state, setState] = useState('idle');
  const recRef = useRef(null);
  const alive = useRef(true);

  useEffect(() => () => {
    alive.current = false;
    const rec = recRef.current;
    if (rec && rec.state === 'recording') rec.stop();
  }, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Voice input is not supported on this browser');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        clearTimeout(rec.maxTimer);
        stream.getTracks().forEach((t) => t.stop());
        if (!alive.current) return;
        setState('busy');
        try {
          const text = await voiceApi.transcribe(new Blob(chunks, { type: rec.mimeType || 'audio/webm' }));
          if (!alive.current) return;
          if (text) onText(text); else toast.error('Didn\u2019t catch that, try again');
        } catch (e) {
          if (alive.current) toast.error(e.response?.data?.detail || 'Could not transcribe');
        } finally { if (alive.current) setState('idle'); }
      };
      rec.start();
      rec.maxTimer = setTimeout(() => rec.state === 'recording' && rec.stop(), 60000);
      recRef.current = rec;
      setState('rec');
    } catch {
      toast.error('Microphone access was blocked');
    }
  };

  const click = () => (state === 'rec' ? recRef.current?.stop() : state === 'idle' && start());

  return (
    <button
      type="button"
      onClick={click}
      disabled={state === 'busy'}
      aria-label={state === 'rec' ? 'Stop recording' : 'Speak'}
      data-testid={testId}
      data-state={state}
      className={`shrink-0 h-11 w-11 rounded-full flex items-center justify-center border transition-colors ${state === 'rec' ? 'bg-[#C05746] border-[#C05746] text-white animate-pulse' : 'bg-white border-[#E5E1D8] text-[#4A7C59] hover:border-[#7E9979]'} ${className}`}
    >
      {state === 'busy' ? <Loader2 className="w-4 h-4 animate-spin" /> : state === 'rec' ? <Square className="w-4 h-4" fill="currentColor" /> : <Mic className="w-4 h-4" strokeWidth={1.5} />}
    </button>
  );
};
